import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  TemplateRef,
  ViewChild,
  OnInit,
  inject,
} from '@angular/core';
import { FieldWrapper } from '@ngx-formly/core';
import { injectDestroy } from 'ngxtension/inject-destroy';
import { merge, takeUntil } from 'rxjs';
import { ClearFieldButtonComponent } from '../../../../../../../shared/components/formly-fields/clear-field-button.component';
import {
  buildClearAriaLabel,
  clearControl,
  clearableIsDisabled,
  clearableIsReadOnly,
  controlHasClearableValue,
} from '../../../../../../../shared/components/formly-fields/clearable-field.type';

/**
 * Adds a "clear field" button to the suffix slot of any Formly Material field
 * that renders through `formly-wrapper-mat-form-field` (i.e. relies on
 * `props.suffix`) — in practice this means the plain `input`/`textarea`/
 * `select` types, which have no DistroDot-owned component to add a button to
 * directly (see `field-config.ts`: `custom: false`). Modeled on
 * `FormlyWrapperAddons` (`unit-labels.ts`) — same `props.suffix` /
 * `<ng-template #matSuffix>` mechanism, just with an interactive clear
 * button instead of a static unit label.
 *
 * Custom field types that own their own inline `<mat-form-field>` template
 * (email, password, custom-number, custom-currency, bsn, time, kvk, ...)
 * never read `props.suffix` — only `formly-wrapper-mat-form-field` does — so
 * this wrapper has no effect on them. Their clear buttons are implemented
 * directly in their own templates by composing the same shared primitive.
 *
 * The button itself follows the filled/blurred state via the shared primitive and global
 * `_forms.scss` rules. Because `props.suffix`'s `<ng-template>` content is
 * teleported via `ngTemplateOutlet` into `formly-wrapper-mat-form-field`'s
 * own template, the rendered button never ends up as a DOM descendant of
 * *this* component's host — `:host-context()` can't reach it. The
 * `mat-form-field` ancestor it does need to react to belongs to that other
 * component's template. The global `dd-clear-suffix` interaction rules live
 * in `_forms.scss`, alongside the shared clear geometry.
 */
@Component({
  selector: 'formly-wrapper-clear-button',
  template: `
    <ng-container #fieldComponent></ng-container>

    <ng-template #matSuffix>
      <span class="dd-clear-suffix">
        <app-clear-field-button
          [hasValue]="hasValue()"
          [disabled]="isDisabled()"
          [readOnly]="isReadOnly()"
          [ariaLabel]="clearAriaLabel()"
          (clearSelection)="clear()" />
      </span>
    </ng-template>
  `,
  imports: [ClearFieldButtonComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormlyWrapperClearButton extends FieldWrapper implements OnInit {
  @ViewChild('matSuffix', { static: true }) matSuffix!: TemplateRef<unknown>;
  readonly #changeDetectorRef = inject(ChangeDetectorRef);
  readonly #destroy$ = injectDestroy();

  /**
   * Assigned in ngOnInit, not ngAfterViewInit: `formly-wrapper-mat-form-field`
   * gates the suffix slot behind `@if (props.suffix)`, and that check runs in the
   * parent's update pass — before any child's AfterViewInit. Assigning later left
   * the button unrendered until something else happened to mark the (OnPush)
   * subtree dirty. `static: true` makes the template available this early.
   */
  ngOnInit() {
    // Addons compose the shared button in their own suffix template. They are
    // the sole owner there; every other generic field is owned by this wrapper.
    if (this.matSuffix && !this.field.wrappers?.includes('addons')) {
      this.props.suffix = this.matSuffix;
    }

    // Zoneless app: props.suffix's @if depends on formControl state, which
    // Formly won't re-check on its own — mirrors CustomSelectComponent's
    // setupValueChanges pattern.
    merge(this.formControl.valueChanges, this.formControl.statusChanges)
      .pipe(takeUntil(this.#destroy$))
      .subscribe(() => this.#changeDetectorRef.markForCheck());
  }

  // A FieldWrapper cannot extend ClearableFieldType (single inheritance, Formly's FieldWrapper
  // is the required base), so the contract is delegated to the shared functions instead.
  hasValue(): boolean {
    return controlHasClearableValue(this.formControl);
  }

  isDisabled(): boolean {
    return clearableIsDisabled(this.formControl, this.props);
  }

  isReadOnly(): boolean {
    return clearableIsReadOnly(this.props);
  }

  clearAriaLabel(): string {
    return buildClearAriaLabel(this.props?.label);
  }

  clear(): void {
    clearControl(this.formControl, this.props);
  }
}
